import { Drawer } from "@made-people/blueprint-drawer";
import { Cross } from "../../../assets/icons/Cross";
import { useState, useEffect } from "react";
import { useSizeGuide } from "./use-size-guide";
import { SizeGuideTabs } from "./size-guide-tabs";
import { BraCharts } from "./bra-charts";
import { PantyCharts } from "./panty-charts";
import { SwimwearCharts } from "./swimwear-charts";
import { StockingsCharts } from "./stockings-charts";
import { CamisoleCharts } from "./camisole-charts";
import { SizeGuideTab } from "./types";
import { createEventEmitter } from "@made-people/grease-event-emitter";
import { overlayEventEmitter } from "@made-people/blueprint-overlay";
import { useTranslate } from "@made-people/grease-translations";

type SizeGuideEvent = {
  openWithProductType: {
    productType?: string;
  };
};

export const sizeGuideEventEmitter = createEventEmitter<SizeGuideEvent>();

/**
 * Helper function to open the size guide programmatically from anywhere.
 *
 * @example
 * ```tsx
 * import { openSizeGuide } from "./components/features/size-guide";
 *
 * <button onClick={() => openSizeGuide("panty")}>
 *   Open Panty Size Guide
 * </button>
 * ```
 */
export const openSizeGuide = (productType?: string) => {
  sizeGuideEventEmitter.emit("openWithProductType", { productType });
  overlayEventEmitter.emit("open", { overlayId: "size-guide" });
};

const mapProductTypeToTab = (productType: string | undefined): SizeGuideTab => {
  if (!productType) {
    return "bra";
  }

  const normalizedType = productType.toLowerCase().trim();

  if (normalizedType.includes("bra") || normalizedType.includes("bh")) {
    return "bra";
  }
  if (
    normalizedType.includes("panty") ||
    normalizedType.includes("tanga") ||
    normalizedType.includes("trosor")
  ) {
    return "panty";
  }
  if (normalizedType.includes("swim") || normalizedType.includes("bikini")) {
    return "swimwear";
  }
  if (
    normalizedType.includes("stocking") ||
    normalizedType.includes("strump")
  ) {
    return "stockings";
  }
  if (
    normalizedType.includes("camisole") ||
    normalizedType.includes("top") ||
    normalizedType.includes("linne")
  ) {
    return "camisoles";
  }

  return "bra";
};

const SizeGuideContent = ({ tab }: { tab: SizeGuideTab }) => {
  switch (tab) {
    case "bra":
      return <BraCharts />;
    case "panty":
      return <PantyCharts />;
    case "swimwear":
      return <SwimwearCharts />;
    case "stockings":
      return <StockingsCharts />;
    case "camisoles":
      return <CamisoleCharts />;
    default:
      return null;
  }
};

export const SizeGuideDrawer = () => {
  const sizeGuide = useSizeGuide();
  const translate = useTranslate();
  const [tab, setTab] = useState<SizeGuideTab>("bra");

  useEffect(() => {
    const handleOpenWithProductType = (
      event: SizeGuideEvent["openWithProductType"],
    ) => {
      const mappedTab = mapProductTypeToTab(event.productType);
      setTab(mappedTab);
    };

    sizeGuideEventEmitter.on("openWithProductType", handleOpenWithProductType);

    return () => {
      sizeGuideEventEmitter.off(
        "openWithProductType",
        handleOpenWithProductType,
      );
    };
  }, []);

  if (!sizeGuide) {
    return null;
  }

  return (
    <Drawer
      customOverlayId="size-guide"
      direction="right"
      hasHandle={false}
      header={{
        headingSettings: {
          heading: translate("Size_Guide_Title"),
        },
        closeIcon: <Cross className="w-3 h-3 absolute right-4 top-4" />,
      }}
    >
      <Drawer.Content
        contentSettings={{
          className:
            "h-full fixed right-0 bottom-0 w-[95%] lg:w-[80%] lg:max-w-[1000px]",
        }}
      >
        <div className="h-full overflow-y-auto pb-10">
          <SizeGuideTabs
            sizeGuide={sizeGuide}
            activeTab={tab}
            onTabChange={setTab}
          />

          <SizeGuideContent tab={tab} />
        </div>
      </Drawer.Content>
    </Drawer>
  );
};
